import { createContext, Dispatch, SetStateAction, useContext } from 'react';

import { LocationOrAllMarketsType } from '~/types/responseTypes';

import { PartialVendorSearch } from '../types/types';

export type NavigateToSrpFunction = (change: PartialVendorSearch) => void;

export type VendorSearchNavigationValues = {
  /** Performs a new search without refreshing the page */
  navigateToSrp: NavigateToSrpFunction;
  tempSelectedLocation?: LocationOrAllMarketsType | null;
  setTempSelectedLocation: Dispatch<SetStateAction<LocationOrAllMarketsType | null | undefined>>;
};

export const DEFAULT_VENDOR_SEARCH_NAVIGATION_CONTEXT_VALUES: VendorSearchNavigationValues = {
  navigateToSrp: () => undefined,
  tempSelectedLocation: null,
  setTempSelectedLocation: () => undefined,
};

export const VendorSearchNavigationContext = createContext<
  VendorSearchNavigationValues | undefined
>(undefined);

/**
 * Encapsulates the logic for navigating on the SRP.  Exposes two pieces of state:
 * - The location (city, state or market)
 * - The taxonomy
 *
 * The search bar does not navigate when the location or taxonomy changes. Instead,
 * it waits until the user clicks the search button. The temporary changes
 * are kept in the tempSelectedLocation and tempSelectedTaxonomy and modified
 * with the corresponding setters.
 *
 * @example
 * const { navigateToSrp } = useVendorSearchNavigation();
 * navigateToSrp({ page: 2 });
 *
 */
export const useVendorSearchNavigation = () => {
  const context = useContext(VendorSearchNavigationContext);
  if (context === undefined) {
    throw new Error(
      'useVendorSearchNavigation must be used within a VendorSearchNavigationContext'
    );
  }
  return context;
};
